import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { Action, FirmType } from '../../../common/models/common.model';
import { Supplier, SupplierCategory, SupplierCategoryRequest } from '../../purchased-order.model';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { finalize, Subscription } from 'rxjs';
import { SupplierService } from '../../services/supplier.service';
import { SupplierCategoryService } from '../../services/supplier-category.service';

@Component({
  selector: 'app-supplier-info-form',
  standalone: false,
  templateUrl: './supplier-info-form.component.html',
  styleUrl: './supplier-info-form.component.scss',
})
export class SupplierInfoFormComponent implements OnInit, OnDestroy {
  @Input() supplier: Supplier | null = null;
  @Input() mode: Action = Action.CREATE;
  @Output() onCancelEvent: EventEmitter<void> = new EventEmitter<void>();
  @Output() onSaveEvent: EventEmitter<Supplier> = new EventEmitter<Supplier>();

  protected supplierForm!: FormGroup;
  protected isSupplierDataSaving: boolean = false;
  protected categories: SupplierCategory[] = [];
  protected submitError: any = null;

  /** The inline "new category" panel, kept in its own group so it never blocks submit. */
  protected newCategoryForm!: FormGroup;
  protected isCreatingCategory: boolean = false;
  protected isSavingCategory: boolean = false;
  protected categoryNameError: string | null = null;
  protected categoryCreateError: any = null;
  protected firmTypeOptions = [
    { label: 'Sole Proprietorship', value: FirmType.SOLE_PROPRIETORSHIP },
    { label: 'Partnership Firm', value: FirmType.PARTNERSHIP_FIRM },
    { label: 'Limited Liability Partnership (LLP)', value: FirmType.LLP },
    { label: 'Private Limited Company (Pvt. Ltd.)', value: FirmType.PRIVATE_LIMITED },
    { label: 'Public Limited Company', value: FirmType.PUBLIC_LIMITED },
    { label: 'One Person Company (OPC)', value: FirmType.OPC },
    { label: 'Co-operative Society', value: FirmType.CO_OPERATIVE_SOCIETY },
    { label: 'Trust / Society', value: FirmType.TRUST_SOCIETY },
  ];

  private _supplierSubscription!: Subscription;
  private _categorySubscription!: Subscription;
  private _categoryCreateSubscription!: Subscription;

  constructor(
    private supplierService: SupplierService,
    private supplierCategoryService: SupplierCategoryService
  ) {}

  ngOnInit(): void {
    this._initializeSupplierForm();
    this._initializeNewCategoryForm();
    this._loadCategories();
  }

  protected startCreatingCategory(): void {
    this._resetNewCategoryForm();
    this.isCreatingCategory = true;
  }

  protected cancelCreatingCategory(): void {
    this.isCreatingCategory = false;
    this._resetNewCategoryForm();
  }

  /**
   * Creates the category and selects it, so classifying a supplier never means leaving
   * this dialog. The name is unique, so a duplicate lands against that field.
   */
  protected saveNewCategory(): void {
    this.newCategoryForm.markAllAsTouched();
    if (this.newCategoryForm.invalid || this.isSavingCategory) return;

    this._clearCategoryErrors();
    this.isSavingCategory = true;

    const reqBody: SupplierCategoryRequest = {
      name: this.newCategoryForm.value.name.trim(),
    };

    this._categoryCreateSubscription = this.supplierCategoryService
      .post(reqBody)
      .pipe(finalize(() => (this.isSavingCategory = false)))
      .subscribe({
        next: (category: SupplierCategory) => {
          // The API sorts by name; keep the dropdown in the same order.
          this.categories = [...this.categories, category].sort((a, b) => a.name.localeCompare(b.name));
          this.supplierForm.get('category')?.setValue(category.id);
          this.cancelCreatingCategory();
        },
        error: (error: HttpErrorResponse) => this._handleCategoryError(error),
      });
  }

  private _handleCategoryError(error: HttpErrorResponse): void {
    this._clearCategoryErrors();

    const body = error?.error;
    if (!body || typeof body !== 'object' || Array.isArray(body)) {
      this.categoryCreateError = error;
      return;
    }

    const rest: Record<string, unknown> = { ...body };

    if (rest['name']) {
      this.categoryNameError = firstErrorMessage(rest['name']);
      delete rest['name'];
    }

    if (Object.keys(rest).length > 0) {
      this.categoryCreateError = { error: rest };
    }
  }

  private _clearCategoryErrors(): void {
    this.categoryNameError = null;
    this.categoryCreateError = null;
  }

  private _resetNewCategoryForm(): void {
    this.newCategoryForm.reset();
    this._clearCategoryErrors();
  }

  private _initializeNewCategoryForm(): void {
    this.newCategoryForm = new FormGroup({
      name: new FormControl(null, [Validators.required, Validators.maxLength(255)]),
    });
  }

  protected onSubmitSupplierForm(): void {
    if (this.supplierForm.invalid) return;

    this.submitError = null;
    this.isSupplierDataSaving = true;

    const reqBody = { ...this.supplierForm.value };

    // Clearing the category has to travel as an explicit null: leaving the key out of
    // a PATCH would keep whatever is already stored.
    reqBody.category = reqBody.category || null;

    const request$ =
      this.supplier && this.mode === Action.EDIT
        ? this.supplierService.patch(this.supplier.id, reqBody)
        : this.supplierService.post(reqBody);

    this._supplierSubscription = request$.pipe(finalize(() => (this.isSupplierDataSaving = false))).subscribe({
      next: (supplier: Supplier) => this.onSaveEvent.emit(supplier),
      error: (error: HttpErrorResponse) => (this.submitError = error),
    });
  }

  private _loadCategories(): void {
    // Unpaginated array, so the whole list is here in one request.
    this._categorySubscription = this.supplierCategoryService.list().subscribe({
      next: (categories: SupplierCategory[]) => (this.categories = categories ?? []),
      // The field is optional; an empty dropdown must not block saving a supplier.
      error: () => undefined,
    });
  }

  protected onCancel(): void {
    this.onCancelEvent.emit();
  }

  private _initializeSupplierForm(): void {
    this.supplierForm = new FormGroup({
      name: new FormControl(this.supplier?.name ?? null, Validators.required),
      responsible_person: new FormControl(this.supplier?.responsible_person ?? null),
      gst_number: new FormControl(this.supplier?.gst_number ?? null),
      pan_card_number: new FormControl(this.supplier?.pan_card_number ?? null),
      udyam_number: new FormControl(this.supplier?.udyam_number ?? null),
      email: new FormControl(this.supplier?.email ?? null, Validators.email),
      phone_number: new FormControl(this.supplier?.phone_number ?? null),
      address: new FormControl(this.supplier?.address ?? null),
      firm_type: new FormControl(this.supplier?.firm_type ?? null),
      // Optional: never validated, and cleared to null rather than left out.
      category: new FormControl(this.supplier?.category ?? null),
    });
  }

  ngOnDestroy(): void {
    this._supplierSubscription?.unsubscribe();
    this._categorySubscription?.unsubscribe();
    this._categoryCreateSubscription?.unsubscribe();
  }
}

function firstErrorMessage(value: unknown): string {
  if (Array.isArray(value)) return String(value[0] ?? '');

  return String(value ?? '');
}
